<template>
  <div class="module-status p-4 bg-gray-100 rounded-lg">
    <h3 class="text-lg font-semibold mb-3">Module Status</h3>
    
    <!-- Active Modules -->
    <div class="mb-4">
      <h4 class="font-medium mb-2">Active Modules:</h4>
      <div class="space-y-2">
        <div v-for="moduleName in activeModules" :key="moduleName" 
             class="flex items-center justify-between p-2 bg-green-100 rounded">
          <span class="font-medium">{{ moduleName }}</span>
          <span class="text-green-600 text-sm">✓ Active</span>
        </div>
        <div v-if="activeModules.length === 0" class="text-gray-500 text-sm">
          No modules active
        </div>
      </div>
    </div>
    
    <!-- Game Module Status -->
    <div v-if="isGameModuleActive" class="mb-4">
      <h4 class="font-medium mb-2">Game Status:</h4>
      <div class="bg-blue-100 p-3 rounded">
        <div class="flex justify-between mb-1">
          <span>Level:</span>
          <span class="font-bold">{{ gameState.level || 1 }}</span>
        </div>
        <div class="flex justify-between mb-1">
          <span>Score:</span>
          <span class="font-bold">{{ gameState.score || 0 }}</span>
        </div>
        <div class="flex justify-between mb-1">
          <span>Points Visited:</span>
          <span class="font-bold">{{ gameState.unlockedPoints?.size || 0 }}</span>
        </div>
        
        <!-- Custom Variables -->
        <div v-if="gameState.variables && Object.keys(gameState.variables).length > 0" class="mt-2 pt-2 border-t border-blue-200">
          <div class="text-sm font-medium mb-1">Custom Variables:</div>
          <div v-for="(value, key) in gameState.variables" :key="key" class="flex justify-between text-sm">
            <span>{{ key }}:</span>
            <span class="font-bold">{{ value }}</span>
          </div>
        </div>
        
        <!-- Inventory -->
        <div v-if="gameState.inventory && Object.keys(gameState.inventory).length > 0" class="mt-2 pt-2 border-t border-blue-200">
          <div class="text-sm font-medium mb-1">Inventory ({{ getTotalInventoryItems() }} items):</div>
          <div v-for="(item, key) in gameState.inventory" :key="key" class="flex items-center justify-between text-sm mb-1">
            <div class="flex items-center">
              <img v-if="item.image?.preview_url" :src="item.image.preview_url" :alt="item.name" class="w-4 h-4 mr-2 rounded" />
              <span>{{ item.quantity }}x {{ item.name || key }}</span>
            </div>
          </div>
        </div>
        <div v-else-if="gameState.inventory" class="mt-2 pt-2 border-t border-blue-200">
          <div class="text-sm text-gray-500">Inventory: Empty</div>
        </div>
        
        <!-- Quiz Answers -->
        <div v-if="gameState.quiz_answers && Object.keys(gameState.quiz_answers).length > 0" class="mt-2 pt-2 border-t border-blue-200">
          <div class="text-sm font-medium mb-1">Quiz Answers ({{ getTotalQuizAnswers() }} answers):</div>
          <div v-for="(pointAnswers, pointId) in gameState.quiz_answers" :key="pointId" class="mb-2">
            <div class="text-xs font-medium text-blue-700">Point {{ pointId }}:</div>
            <div v-for="(answer, questionId) in pointAnswers" :key="questionId" class="ml-2 text-xs">
              <span class="text-gray-600">Q{{ questionId }}:</span>
              <span class="font-medium">{{ answer }}</span>
            </div>
          </div>
        </div>
        <div v-else-if="gameState.quiz_answers" class="mt-2 pt-2 border-t border-blue-200">
          <div class="text-sm text-gray-500">Quiz Answers: None</div>
        </div>
      </div>
      
      <!-- Locked/Unlocked Points -->
      <div class="mt-2">
        <h5 class="font-medium text-sm mb-1">Point Status:</h5>
        <div class="text-xs space-y-1">
          <div v-if="gameState.unlockedPoints && gameState.unlockedPoints.size > 0">
            <div class="text-green-600 font-medium">Unlocked ({{ gameState.unlockedPoints.size }}):</div>
            <div class="ml-2 text-green-700">
              {{ Array.from(gameState.unlockedPoints).join(', ') }}
            </div>
          </div>
          <div v-if="lockedPoints.length > 0">
            <div class="text-red-600 font-medium">Locked ({{ lockedPoints.length }}):</div>
            <div class="ml-2 text-red-700">
              {{ lockedPoints.join(', ') }}
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Routing Module Status -->
    <div v-if="isRoutingModuleActive" class="mb-4">
      <h4 class="font-medium mb-2">Navigation Status:</h4>
      <div class="bg-purple-100 p-3 rounded">
        <div v-if="routingState.currentRoute" class="space-y-1">
          <div class="flex justify-between">
            <span>Current Route:</span>
            <span class="font-bold">{{ routingState.currentRoute.routeType }}</span>
          </div>
          <div class="flex justify-between">
            <span>Progress:</span>
            <span class="font-bold">{{ routingState.currentRoute.progress || 0 }}%</span>
          </div>
        </div>
        <div v-else class="text-gray-600 text-sm">
          No active navigation
        </div>
      </div>
    </div>
    
    <!-- Module Actions -->
    <div class="space-y-2">
      <h4 class="font-medium">Module Actions:</h4>
      
      <!-- Game Actions -->
      <div v-if="isGameModuleActive" class="space-y-2">
        <button @click="testGameEvent" 
                class="w-full bg-blue-500 text-white px-3 py-2 rounded hover:bg-blue-600 text-sm">
          Test Quiz (Perfect Score)
        </button>
        <button @click="addScore" 
                class="w-full bg-green-500 text-white px-3 py-2 rounded hover:bg-green-600 text-sm">
          Add 10 Points
        </button>
        <button @click="setCustomVariable" 
                class="w-full bg-yellow-500 text-white px-3 py-2 rounded hover:bg-yellow-600 text-sm">
          Set Custom Variable
        </button>
        <button @click="resetGameState" 
                class="w-full bg-red-500 text-white px-3 py-2 rounded hover:bg-red-600 text-sm">
          Reset Game State
        </button>
        <button @click="createSampleData" 
                class="w-full bg-indigo-500 text-white px-3 py-2 rounded hover:bg-indigo-600 text-sm">
          Create Sample Data
        </button>
        <button @click="testApiAction" 
                class="w-full bg-pink-500 text-white px-3 py-2 rounded hover:bg-pink-600 text-sm">
          Test API Action
        </button>
        <button @click="addInventoryItem" 
                class="w-full bg-orange-500 text-white px-3 py-2 rounded hover:bg-orange-600 text-sm">
          Add Test Item
        </button>
        <button @click="testObjectAction" 
                class="w-full bg-teal-500 text-white px-3 py-2 rounded hover:bg-teal-600 text-sm">
          Test Object Action
        </button>
        <button @click="debugModuleStatus" 
                class="w-full bg-gray-500 text-white px-3 py-2 rounded hover:bg-gray-600 text-sm">
          Debug Module Status
        </button>
        <button @click="clearQuizAnswers" 
                class="w-full bg-red-600 text-white px-3 py-2 rounded hover:bg-red-700 text-sm">
          Clear Quiz Answers
        </button>
        <button @click="testQuizAnswerStorage" 
                class="w-full bg-purple-600 text-white px-3 py-2 rounded hover:bg-purple-700 text-sm">
          Test Quiz Answer Storage
        </button>
        <button @click="testInventoryChangeModal" 
                class="w-full bg-cyan-600 text-white px-3 py-2 rounded hover:bg-cyan-700 text-sm">
          Test Inventory Change Modal
        </button>
        <button @click="testInventoryRemoveModal" 
                class="w-full bg-amber-600 text-white px-3 py-2 rounded hover:bg-amber-700 text-sm">
          Test Inventory Remove Modal
        </button>
      </div>
      
      <!-- Routing Actions -->
      <div v-if="isRoutingModuleActive" class="space-y-2">
        <button @click="testNavigation" 
                class="w-full bg-purple-500 text-white px-3 py-2 rounded hover:bg-purple-600">
          Test Navigation
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import moduleManager from '../modules/index.js'

export default {
  name: 'ModuleStatus',
  data() {
    return {
      activeModules: []
    }
  },
  computed: {
    isGameModuleActive() {
      return moduleManager.isModuleActive('game')
    },
    
    isRoutingModuleActive() {
      return moduleManager.isModuleActive('routing')
    },
    
    gameState() {
      return moduleManager.getModuleData('game')
    },
    
    routingState() {
      return moduleManager.getModuleData('routing')
    },
    
    // Get locked points (points that exist but are not in unlockedPoints)
    lockedPoints() {
      // This would need to be passed from the Points component
      // For now, return empty array
      return []
    }
  },
  mounted() {
    this.updateActiveModules()
    
    // Listen for module state changes
    moduleManager.addEventListener('game:state_updated', this.updateActiveModules.bind(this))
    moduleManager.addEventListener('routing:navigation_started', this.updateActiveModules.bind(this))
    moduleManager.addEventListener('game:inventory_updated', this.updateActiveModules.bind(this))
  },
  methods: {
    updateActiveModules() {
      this.activeModules = moduleManager.getActiveModules()
    },
    
    testGameEvent() {
      // Simulate a quiz submission
      moduleManager.dispatchEvent('quiz:submitted', {
        pointId: 'test-point',
        answers: { q1: 'A', q2: 'B' },
        correctAnswers: { q1: 'A', q2: 'B' }
      })
    },
    
    testNavigation() {
      // Request navigation to a test destination
      moduleManager.dispatchEvent('navigation:request', {
        destination: { latitude: 52.3676, longitude: 4.9041 },
        routeType: 'walking'
      })
    },
    
    addScore() {
      // Add 10 points to the game state
      const gameModule = moduleManager.getModuleData('game')
      const newScore = (gameModule.score || 0) + 10
      
      // Update score through module manager
      moduleManager.setModuleData('game', {
        ...gameModule,
        score: newScore
      })
      
      console.log(`[ModuleStatus] Added 10 points. New score: ${newScore}`)
    },
    
    setCustomVariable() {
      // Set a custom variable for testing conditions
      const gameModule = moduleManager.getModuleData('game')
      const testValue = Math.floor(Math.random() * 10) + 1
      
      moduleManager.setModuleData('game', {
        ...gameModule,
        variables: {
          ...gameModule.variables,
          test_var: testValue
        }
      })
      
      console.log(`[ModuleStatus] Set custom variable 'test_var' to ${testValue}`)
    },
    
    resetGameState() {
      // Reset the game state to initial values
      moduleManager.setModuleData('game', {
        score: 0,
        level: 1,
        unlockedPoints: new Set(),
        variables: {}
      })
      
      console.log('[ModuleStatus] Game state reset to initial values')
    },
    
    createSampleData() {
      // Create sample test data for demonstration
      const gameModule = moduleManager.getModuleData('game')
      if (gameModule && gameModule.createSampleTestData) {
        const sampleData = gameModule.createSampleTestData()
        console.log('[ModuleStatus] Sample data created:', sampleData)
        
        // Dispatch points loaded event with sample data
        moduleManager.dispatchEvent('points:loaded', {
          points: sampleData,
          rawResponse: Object.values(sampleData)
        })
      }
    },
    
    testApiAction() {
      // Test the new API action structure
      console.log('[ModuleStatus] Testing API action structure')
      
      const testPointData = {
        point: { id: 'test-point' },
        data: {
          point: { id: 'test-point' },
          actions: [
            {
              id: 999,
              type: 'variable',
              data: {
                type: { id: 1, name: 'score', image: [] },
                operation: 'increase',
                value: '3',
                trigger: 'every_time'
              },
              operation: 'increase',
              trigger: 'every_time',
              value: '3'
            }
          ]
        }
      }
      
      // Dispatch point opening event to test action execution
      moduleManager.dispatchEvent('point:opening', {
        pointId: 'test-point',
        pointData: testPointData,
        showPreview: false
      })
      
      console.log('[ModuleStatus] API action test completed')
    },
    
    addInventoryItem() {
      // Add a test item to inventory
      console.log('[ModuleStatus] Adding new item to inventory')
      
      const testItemId = `test-item-${Date.now()}`
      const testItemData = {
        name: 'Test Item',
        description: 'A test item for the inventory',
        image: 'https://via.placeholder.com/32x32/4CAF50/FFFFFF?text=T'
      }
      
      // Dispatch an object action to add the item
      moduleManager.dispatchEvent('point:opening', {
        pointId: 'inventory-add',
        pointData: {
          point: { id: 'inventory-add' },
          data: {
            actions: [
              {
                id: 'add-action',
                type: 'object',
                operation: 'add',
                objectId: testItemId,
                objectData: testItemData
              }
            ]
          }
        },
        showPreview: false
      })
    },
    
    testObjectAction() {
      // Test object action with a more complex item
      console.log('[ModuleStatus] Testing object action')
      
      const testItemId = 'magic-key'
      const testItemData = {
        name: 'Magic Key',
        description: 'A mysterious key that unlocks hidden doors',
        image: 'https://via.placeholder.com/32x32/FF9800/FFFFFF?text=🔑',
        rarity: 'rare',
        power: 10
      }
      
      // Dispatch an object action to add the magic key
      moduleManager.dispatchEvent('point:opening', {
        pointId: 'magic-key-point',
        pointData: {
          point: { id: 'magic-key-point' },
          data: {
            actions: [
              {
                id: 'magic-key-action',
                type: 'object',
                operation: 'add',
                objectId: testItemId,
                objectData: testItemData
              }
            ]
          }
        },
        showPreview: false
      })
    },
    
    debugModuleStatus() {
      console.log('=== MODULE STATUS DEBUG ===')
      
      // Check if game module is active
      const isGameActive = moduleManager.isModuleActive('game')
      console.log('Game module active:', isGameActive)
      
      // Check game state
      const gameState = moduleManager.getModuleData('game')
      console.log('Game state:', gameState)
      
      // Check event listeners
      const eventListeners = moduleManager.getEventListeners()
      console.log('All event listeners:', Array.from(eventListeners.entries()))
      
      // Check point:opening listeners specifically
      const pointOpeningListeners = eventListeners.get('point:opening')
      console.log('point:opening listeners:', pointOpeningListeners)
      
      if (pointOpeningListeners) {
        pointOpeningListeners.forEach((listener, index) => {
          console.log(`Listener ${index + 1}:`, {
            moduleName: listener.moduleName,
            callback: listener.callback.name || 'anonymous'
          })
        })
      }
      
      // Test direct event dispatch
      console.log('Testing direct event dispatch...')
      const result = moduleManager.dispatchEvent('point:opening', {
        pointId: 'debug-test',
        pointData: {
          point: { id: 'debug-test' },
          data: {
            actions: [
              {
                id: 999,
                type: 'variable',
                data: {
                  type: { id: 1, name: 'score', image: [] },
                  operation: 'increase',
                  value: '1',
                  trigger: 'every_time'
                },
                operation: 'increase',
                trigger: 'every_time',
                value: '1'
              }
            ]
          }
        },
        showPreview: false
      })
      
      console.log('Direct event dispatch result:', result)
      console.log('=== END DEBUG ===')
    },
    getTotalInventoryItems() {
      if (!this.gameState.inventory) return 0
      return Object.values(this.gameState.inventory).reduce((total, item) => total + (item.quantity || 0), 0)
    },
    getTotalQuizAnswers() {
      if (!this.gameState.quiz_answers) return 0
      return Object.keys(this.gameState.quiz_answers).length
    },
    clearQuizAnswers() {
      // Reset quiz answers to empty object
      moduleManager.setModuleData('game', {
        ...this.gameState,
        quiz_answers: {}
      })
      
      console.log('[ModuleStatus] Quiz answers cleared')
    },
    testQuizAnswerStorage() {
      // Test quiz answer storage by adding some sample answers
      const gameModule = moduleManager.getModuleData('game')
      const testAnswers = {
        ...gameModule.quiz_answers,
        'test-point-1': {
          'question-1': 'A',
          'question-2': 'B'
        },
        'test-point-2': {
          'question-3': 'C'
        }
      }
      
      moduleManager.setModuleData('game', {
        ...gameModule,
        quiz_answers: testAnswers
      })
      
      console.log('[ModuleStatus] Test quiz answers added:', testAnswers)
    },
    testInventoryChangeModal() {
      // Test the inventory change modal directly
      console.log('[ModuleStatus] Testing inventory change modal')
      
      // Simulate adding an item to inventory
      const testItemId = 'test-modal-item'
      const testItemData = {
        name: 'Test Modal Item',
        description: 'This is a test item for the modal',
        image: 'https://via.placeholder.com/32x32/2196F3/FFFFFF?text=M'
      }
      
      // Get the game module and add the item
      const gameModule = moduleManager.getModuleData('game')
      if (gameModule && gameModule.addObjectToInventory) {
        gameModule.addObjectToInventory(testItemId, testItemData, 'test-point', true)
        console.log('[ModuleStatus] Test item added to inventory')
      } else {
        console.warn('[ModuleStatus] Game module not available for testing')
      }
    },
    testInventoryRemoveModal() {
      // Test the inventory remove modal directly
      console.log('[ModuleStatus] Testing inventory remove modal')
      
      // Simulate removing an item from inventory
      const testItemId = 'test-remove-item'
      const testItemData = {
        name: 'Test Remove Item',
        description: 'This is a test item for the remove modal',
        image: 'https://via.placeholder.com/32x32/2196F3/FFFFFF?text=R'
      }
      
      // Get the game module and remove the item
      const gameModule = moduleManager.getModuleData('game')
      if (gameModule && gameModule.removeObjectFromInventory) {
        gameModule.removeObjectFromInventory(testItemId, 'test-point', true)
        console.log('[ModuleStatus] Test item removed from inventory')
      } else {
        console.warn('[ModuleStatus] Game module not available for testing')
      }
    }
  }
}
</script> 